"use client";

import { Auth } from "@/components/Auth";
import { AppProvider, useAppContext } from "@/context/AppContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SessionProvider } from "@/context/SessionContext";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ComponentType, useEffect, useState } from "react";
import { Toaster } from "sonner";

const TOAST_DURATION_MS = 4000;

function AppContent({ children }: { children: React.ReactNode }) {
  const { isLoggedIn } = useAppContext();
  const [AuthenticatedProviders, setAuthenticatedProviders] =
    useState<ComponentType<{ children: React.ReactNode }> | null>(null);

  useEffect(() => {
    if (!isLoggedIn || AuthenticatedProviders) return;

    void import("@/components/providers/AuthenticatedProviders").then(
      (module) => setAuthenticatedProviders(() => module.default),
    );
  }, [AuthenticatedProviders, isLoggedIn]);

  const content = (
    <Auth>
      {children}
      <Toaster
        position="top-center"
        duration={TOAST_DURATION_MS}
        closeButton
        icons={{
          close: <XMarkIcon aria-hidden="true" />,
        }}
      />
    </Auth>
  );

  if (isLoggedIn && !AuthenticatedProviders) {
    return (
      <main className="min-h-screen" aria-busy="true" aria-label="Načítání účtu" />
    );
  }

  return isLoggedIn && AuthenticatedProviders ? (
    <AuthenticatedProviders>{content}</AuthenticatedProviders>
  ) : (
    content
  );
}

export function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <LanguageProvider>
      <AppProvider>
        <SessionProvider>
          <AppContent>{children}</AppContent>
        </SessionProvider>
      </AppProvider>
    </LanguageProvider>
  );
}
